import { FC } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import OneHealthLogo from "../../assets/icons/onehealthLogo.svg?react";

interface OneHealthWordmarkProps {
    /** Rendered width; the height follows the 4.8:1 aspect ratio. Omit both to keep the intrinsic size. */
    width?: number;
    /** Rendered height; the width follows the aspect ratio. Omit both to keep the intrinsic size. */
    height?: number;
    sx?: SxProps<Theme>;
}

/**
 * Size the mark from whichever dimension the caller gave, letting the other follow the aspect ratio.
 *
 * The asset carries intrinsic `width`/`height` attributes, so constraining only one axis in CSS leaves
 * the other pinned at its original value — the mark then letterboxes inside a box of the wrong shape
 * instead of scaling. Setting the free axis to `auto` overrides the attribute and lets it scale.
 */
const resolveSize = (width?: number, height?: number) => {
    if (width === undefined && height === undefined) return {};
    return { width: width ?? "auto", height: height ?? "auto" };
};

/**
 * The 1health wordmark, rendered in the current text colour (NG-8606).
 *
 * The asset hard-codes `fill:#464B52 !important` inside an embedded `<style>`, so on its own the
 * wordmark ignores its surroundings and paints dark slate — near-invisible in the app's dark mode and
 * on any tenant's dark `formBackground`. Beating an `!important` declaration needs both `!important`
 * and greater specificity: the rule emitted here is `.css-hash .logoOneHealth`, which outranks the
 * asset's bare `.logoOneHealth`.
 *
 * Re-colouring the SVG itself would be tidier, but the same file is imported in several places and a
 * fixed fill would only move the problem to whichever background it was not chosen for. Driving it
 * from `currentColor` lets each caller's text colour decide.
 *
 * Rendered through `Box component=` rather than MUI's `SvgIcon`: SvgIcon forces `width/height: 1em`,
 * which would squash this 4.8:1 wordmark into a square box wherever a caller wants the intrinsic size.
 */
const OneHealthWordmark: FC<OneHealthWordmarkProps> = ({ width, height, sx }) => (
    <Box
        component={OneHealthLogo}
        role="img"
        aria-label="1health"
        sx={[
            {
                color: "text.primary",
                "& .logoOneHealth": { fill: "currentColor !important" },
                ...resolveSize(width, height),
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ]}
    />
);

export default OneHealthWordmark;
